import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  DataTable,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  useToast,
  FilterBar,
  SavedFiltersMenu,
  ListSummaryBar,
} from '@bbpos/ui';
import type { ColumnDef, FilterBarFacet } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import { useQuotations, useConversionRate, quotationsApi, QuotationStatusBadge, QuotationResponseHint, QuotationHero, QuotationKpi } from '@/features/quotations';
import type { Quotation, QuotationStatus } from '@/features/quotations';
import { useListFilterBar } from '@/features/saved-filters';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { downloadFile } from '@/lib/download-file';
import { ROLE_LEVELS } from '@/types/user';
import { useKeyboardShortcut } from '@/hooks/use-keyboard-shortcut';
import { posUrl } from '@/lib/pos-url';
import { GLASS } from '@/constants/design-tokens';
import {
  FileTextIcon,
  ClockIcon,
  CheckCircleIcon,
  TrendingUpIcon,
  ChartIcon,
  EyeIcon,
  SendIcon,
  ArrowRightIcon,
  DownloadIcon,
  PlusIcon,
} from './icons';

// ─── Constants ──────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'CONVERTED', label: 'Converted' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'REJECTED', label: 'Declined' },
];

// T27: the shared filter bar. Keys are the list endpoint's own parameters.
export const QUOTATION_FILTER_KEYS = [
  'customer_name',
  'mobile',
  'quote_number',
  'search',
  'status',
  'start_date',
  'end_date',
];
const QUOTATION_FACETS: FilterBarFacet[] = [
  { key: 'customer_name', label: 'Customer' },
  { key: 'mobile', label: 'Mobile', kind: 'phone' },
  { key: 'quote_number', label: 'Quote no.', kind: 'number' },
  { key: 'search', label: 'Any of them' },
];
const QUOTATION_DATE = { fromKey: 'start_date', toKey: 'end_date' };

// ─── Validity helpers ───────────────────────────────────────────────────────

// A quote past its valid_until can no longer be converted — the backend rejects
// it with 409 "Quotation has expired". The convert entry points must mirror that
// guard so the user is never routed into a dead-end conversion flow.
export function isQuotationExpired(validUntil: string, now: number = Date.now()): boolean {
  return new Date(validUntil).getTime() < now;
}

// The `c` shortcut converts the top convertible quote. A quote is convertible
// when its (effective) status is ACTIVE or APPROVED and it has not expired.
// APPROVED-but-expired quotes are the key gap: the list recomputes ACTIVE→EXPIRED
// server-side, but APPROVED rows keep their status, so without this guard the
// shortcut would still target an expired quote the backend then rejects.
export function pickTopConvertible<T extends { status: string; valid_until: string }>(
  quotations: T[],
  now: number = Date.now(),
): T | undefined {
  return quotations.find(
    (q) =>
      (q.status === 'ACTIVE' || q.status === 'APPROVED') && !isQuotationExpired(q.valid_until, now),
  );
}

function validityColor(validUntil: string): string {
  const now = Date.now();
  const expiry = new Date(validUntil).getTime();
  const twoDays = 2 * 24 * 60 * 60 * 1000;
  if (expiry < now) return 'text-danger-600 dark:text-danger-400';
  if (expiry - now < twoDays) return 'text-warning-600 dark:text-warning-400';
  return 'text-neutral-700 dark:text-neutral-300';
}

// ─── Content ────────────────────────────────────────────────────────────────

export function QuotationListContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const profile = useAuthStore((s) => s.profile);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // The range is the store's calendar days (T20): the header's store's zone,
  // else the business's — the same day the API filters on.
  const bar = useListFilterBar({
    page: 'quotations',
    keys: QUOTATION_FILTER_KEYS,
    date: QUOTATION_DATE,
    locationId: selectedLocationId,
  });
  const { values } = bar;
  const page = Number(searchParams.get('page') ?? '1');
  const hasFilters = Object.keys(values).length > 0;

  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const isManager = roleLevel >= ROLE_LEVELS.MANAGER;

  const [isExporting, setIsExporting] = useState(false);

  const setPage = (p: number) => bar.onChange({ page: String(p) });

  const listParams = {
    status: values.status as QuotationStatus | undefined,
    search: values.search,
    customer_name: values.customer_name,
    mobile: values.mobile,
    quote_number: values.quote_number,
    start_date: values.start_date,
    end_date: values.end_date,
    location_id: selectedLocationId ?? undefined,
  };
  const { data, isLoading, error } = useQuotations({ ...listParams, page, limit: 20 });

  const { data: conversionData } = useConversionRate(
    {
      location_id: selectedLocationId,
      start_date: values.start_date,
      end_date: values.end_date,
    },
    isManager,
  );

  useEffect(() => {
    if (error) {
      toast({ title: (error as Error).message, variant: 'error' });
    }
  }, [error, toast]);

  // Keyboard shortcuts: `/` focus search, `n` new quote, `c` convert top active
  const focusSearch = useCallback(() => {
    searchInputRef.current?.focus();
    searchInputRef.current?.select();
  }, []);
  // Quote creation lives in the POS app, which is a separate origin from the
  // admin dashboard. A bare `/pos/billing` (via navigate() or a relative
  // window.open) resolves against the admin origin, which has no such route —
  // the admin catch-all then traps the user on a "Page coming soon..."
  // dead-end that looks like an indefinitely-loading page (issue #56).
  // posUrl() resolves the POS origin so the new tab lands on the real app.
  const newQuote = useCallback(() => {
    window.open(posUrl('/pos/billing?mode=quote'), '_blank', 'noopener,noreferrer');
  }, []);
  const convertTop = useCallback(() => {
    const all = data?.data ?? [];
    const top = pickTopConvertible(all);
    if (top) {
      navigate(`/quotations/${top.id}?action=convert`);
      return;
    }
    // Distinguish "nothing to convert" from "the candidates have all expired" so
    // the user gets an accurate, upfront reason instead of a silent no-op.
    const hasExpiredCandidate = all.some(
      (q) =>
        (q.status === 'ACTIVE' || q.status === 'APPROVED') && isQuotationExpired(q.valid_until),
    );
    toast(
      hasExpiredCandidate
        ? { title: 'Top active quote has expired and cannot be converted', variant: 'warning' }
        : { title: 'No active quote to convert', variant: 'info' },
    );
  }, [data, navigate, toast]);
  useKeyboardShortcut('/', focusSearch);
  useKeyboardShortcut('n', newQuote);
  useKeyboardShortcut('c', convertTop);

  // Export the entire filtered result set, not just the current page (issue
  // #205): the API writes every matching quote — the same filters, all pages (T27).
  const handleExport = async () => {
    setIsExporting(true);
    try {
      downloadFile(await quotationsApi.exportCsv(listParams), 'quotations.csv');
    } catch (err) {
      toast({ title: (err as Error).message || 'Export failed', variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  const quotations = data?.data ?? [];
  const total = data?.meta?.total ?? 0;

  const totalQuotes = conversionData?.total_quotes ?? total;
  const activeCount = conversionData?.active ?? 0;
  const convertedCount = conversionData?.converted ?? 0;
  const conversionRate = conversionData?.conversion_rate_pct ?? 0;

  const columns: ColumnDef<Quotation>[] = useMemo(
    () => [
      {
        key: 'quote_number',
        header: 'Quote #',
        render: (_v, row) => (
          <button
            type="button"
            onClick={() => navigate(`/quotations/${row.id}`)}
            className="font-mono text-sm text-brand-600 dark:text-brand-400 hover:underline tabular-nums text-left font-medium"
          >
            {row.quote_number}
          </button>
        ),
      },
      {
        key: 'created_at',
        header: 'Date',
        render: (_v, row) => (
          <span className="font-mono text-sm text-neutral-700 dark:text-neutral-300 tabular-nums">
            {new Date(row.created_at).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </span>
        ),
      },
      {
        key: 'customer_name',
        header: 'Customer',
        render: (_v, row) => (
          <span className="text-sm text-neutral-700 dark:text-neutral-300">
            {row.customer_name || (
              <span className="italic text-neutral-400 dark:text-neutral-500">Walk-in</span>
            )}
          </span>
        ),
      },
      {
        key: 'grand_total',
        header: 'Total',
        numeric: true,
        render: (_v, row) => (
          <span className="font-mono text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
            {formatCurrency(row.grand_total, currency)}
          </span>
        ),
      },
      {
        key: 'valid_until',
        header: 'Valid Until',
        render: (_v, row) => (
          <span className={`font-mono text-sm tabular-nums ${validityColor(row.valid_until)}`}>
            {new Date(row.valid_until).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (_v, row) => (
          <div className="flex flex-col items-start gap-1">
            <QuotationStatusBadge status={row.status} />
            <QuotationResponseHint quotation={row} />
          </div>
        ),
      },
      {
        key: 'actions',
        header: '',
        render: (_v, row) => (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => navigate(`/quotations/${row.id}`)}
              className="p-1.5 rounded-lg text-neutral-500 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors"
              title="View"
              aria-label={`View ${row.quote_number}`}
            >
              <EyeIcon />
            </button>
            {(row.status === 'ACTIVE' || row.status === 'APPROVED') && (
              <button
                type="button"
                onClick={() => navigate(`/quotations/${row.id}?action=send`)}
                className="p-1.5 rounded-lg text-neutral-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                title="Send Quote"
                aria-label={`Send ${row.quote_number}`}
              >
                <SendIcon />
              </button>
            )}
            {row.status === 'ACTIVE' && !isQuotationExpired(row.valid_until) && (
              <button
                type="button"
                onClick={() => navigate(`/quotations/${row.id}?action=convert`)}
                className="p-1.5 rounded-lg text-neutral-500 hover:text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20 transition-colors"
                title="Convert to Sales Order"
                aria-label={`Convert ${row.quote_number} to Sales Order`}
              >
                <ArrowRightIcon />
              </button>
            )}
          </div>
        ),
      },
    ],
    [navigate],
  );

  const heroRight = (
    <div className="flex flex-wrap items-center gap-2">
      {isManager && (
        <Button
          variant="secondary"
          onClick={handleExport}
          className="bg-white/15 hover:bg-white/25 text-white border-white/20"
          disabled={quotations.length === 0 || isExporting}
        >
          <DownloadIcon className="h-4 w-4 mr-1.5" />
          {isExporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      )}
      {isManager && (
        <Button
          variant="secondary"
          onClick={() => navigate('/quotations/analytics')}
          className="bg-white/15 hover:bg-white/25 text-white border-white/20"
        >
          <ChartIcon className="h-4 w-4 mr-1.5" />
          Analytics
        </Button>
      )}
      <Button
        variant="secondary"
        onClick={newQuote}
        className="bg-white text-brand-700 hover:bg-brand-50 border-transparent shadow-sm"
      >
        <PlusIcon className="h-4 w-4 mr-1.5" />
        New Quote
      </Button>
    </div>
  );

  // ── Loading skeleton ────────────────────────────────────────────────────────

  if (isLoading && !data) {
    return (
      <>
        <Skeleton variant="rect" height={104} className="rounded-2xl mb-6" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={120} className="rounded-2xl" />
          ))}
        </div>
        <Skeleton variant="rect" height={56} className="rounded-xl mb-4" />
        <Skeleton variant="rect" height={400} className="rounded-2xl" />
      </>
    );
  }

  // ── Empty state (no filters applied) ────────────────────────────────────────

  if (!isLoading && total === 0 && !hasFilters) {
    return (
      <>
        <QuotationHero
          title="Quotations"
          subtitle="Manage customer quotes and track conversions"
          right={heroRight}
        />
        <EmptyState
          variant="page"
          icon={<FileTextIcon className="h-full w-full" />}
          title="No quotations yet"
          description="Quotations created from the POS will appear here. Send price estimates to customers and track conversions."
          action={{ label: 'New Quote', onClick: newQuote }}
        />
      </>
    );
  }

  return (
    <>
      <QuotationHero
        title="Quotations"
        subtitle={
          <span className="inline-flex items-center gap-2">
            <span>Manage customer quotes and track conversions</span>
            <span className="hidden md:inline text-brand-200/80 text-xs">
              · <kbd className="font-mono px-1 py-0.5 bg-white/15 rounded">/</kbd> search ·{' '}
              <kbd className="font-mono px-1 py-0.5 bg-white/15 rounded">n</kbd> new ·{' '}
              <kbd className="font-mono px-1 py-0.5 bg-white/15 rounded">c</kbd> convert
            </span>
          </span>
        }
        right={heroRight}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
        <QuotationKpi
          tone="brand"
          title="Total Quotes"
          value={totalQuotes.toLocaleString('en-IN')}
          icon={<FileTextIcon />}
          delay={0}
        />
        <QuotationKpi
          tone="indigo"
          title="Active"
          value={isManager ? activeCount.toLocaleString('en-IN') : '—'}
          subtext={isManager ? 'in pipeline' : 'manager view only'}
          icon={<ClockIcon />}
          delay={50}
        />
        <QuotationKpi
          tone="success"
          title="Converted"
          value={isManager ? convertedCount.toLocaleString('en-IN') : '—'}
          subtext={isManager && totalQuotes > 0 ? `${convertedCount} of ${totalQuotes}` : undefined}
          icon={<CheckCircleIcon />}
          delay={100}
        />
        <QuotationKpi
          tone="violet"
          title="Conversion Rate"
          value={isManager ? `${conversionRate.toFixed(1)}%` : '—'}
          subtext={isManager ? 'closed quotes ÷ total' : undefined}
          icon={<TrendingUpIcon />}
          delay={150}
        />
      </div>

      {/* Filters (T27) */}
      <div className="mb-4">
        <FilterBar
          facets={QUOTATION_FACETS}
          selects={[{ key: 'status', label: 'Status', options: STATUS_OPTIONS, allLabel: 'All statuses' }]}
          date={QUOTATION_DATE}
          values={values}
          onChange={bar.onChange}
          onReset={bar.onReset}
          timeZone={bar.timeZone}
          inputRef={searchInputRef}
          placeholder="Type a customer, mobile or quote number…  ( press / )"
          favorites={<SavedFiltersMenu {...bar.favorites} />}
        />
      </div>

      {/* Every matching quote, not the page — the export's count and sum. */}
      <div className="mb-4">
        <ListSummaryBar
          loading={isLoading}
          items={[
            { label: 'Quotes matching', value: total.toLocaleString('en-IN'), tone: 'brand' },
            {
              label: 'Quoted value',
              value: formatCurrency(data?.meta?.summary?.grand_total ?? 0, currency),
              tone: 'violet',
            },
          ]}
        />
      </div>

      {/* Data Table */}
      <div className={`${GLASS} animate-slide-up`}>
        <DataTable<Quotation>
          columns={columns}
          data={quotations}
          loading={isLoading}
          totalCount={total}
          currentPage={page}
          pageSize={20}
          onPageChange={setPage}
          emptyState={{
            title: 'No quotations match filters',
            description: 'Try adjusting your filters or date range',
          }}
        />
      </div>
    </>
  );
}

// ─── Page export ────────────────────────────────────────────────────────────

export function QuotationListPage() {
  return (
    <PageContainer title="Quotations" breadcrumbs={[{ label: 'Quotations' }]}>
      <ErrorBoundary>
        <QuotationListContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
